"use client";

import { Box, Checkbox, Text, Title } from "@mantine/core";
import clsx from "clsx";
import {
  SubTopic,
  Topic,
  TopicSyllabusPointMap,
} from "@tsTypes/subject-schema";
import { useMemo } from "react";
import {
  selectTopicSyllabusMetaById,
  topicSyllabusSelection,
} from "../../../../../../features/syllabus/subjectSyllabusChecklistSlice";
import {
  useCustomDispatch,
  useCustomSelector,
} from "../../../../../../lib/redux/hooks";
import { stripParagraphTag } from "../../../../../../lib/strapi/formatters";
import { useTopicMeta } from "../../../../../components/CoreDataProvider";
import classes from "./SyllabusTable.module.css";
import SyllabusRow from "./SyllabusRow";

const newLayout = ["25%", "75%"];

export default function SyllabusTable({
  subjectNameLower,
  subjectDocumentId,
  topicSyllabusPointData,
  withoutMargins,
  restrictAccess,
  className,
}: {
  subjectNameLower: string;
  subjectDocumentId: string;
  topicSyllabusPointData: {
    documentId: Topic["documentId"];
    TopicDescription: Topic["TopicDescription"];
    RelatedSubTopics: Pick<SubTopic, "documentId" | "RelatedSyllabusPoints">[];
  };
  withoutMargins?: boolean;
  restrictAccess: boolean;
  className?: string;
}) {
  const topicSyllabusPointMap = createTopicSyllabusPointMap(
    topicSyllabusPointData.RelatedSubTopics
  );
  const topicMeta = useTopicMeta(topicSyllabusPointData.documentId);

  const dispatch = useCustomDispatch();

  const topicPointMeta = useCustomSelector((state) =>
    restrictAccess
      ? undefined
      : selectTopicSyllabusMetaById(
          state,
          subjectDocumentId,
          topicSyllabusPointData.documentId
        )
  );

  const topicTotalPoints = topicSyllabusPointMap.total;
  const isComplete = topicPointMeta?.selectedTotal === topicTotalPoints;
  const completionPercentage =
    ((topicPointMeta?.selectedTotal ?? 0) / topicTotalPoints) * 100;

  const progressColor =
    completionPercentage === 100
      ? `linear-gradient(90deg, var(--mantine-color-darkblue-3), var(--mantine-color-darkblue-6))`
      : `linear-gradient(90deg, var(--mantine-color-lightblue-3), var(--mantine-color-darkblue-3))`;

  const syllabusRows = useMemo(
    () =>
      topicSyllabusPointData.RelatedSubTopics.map((subTopic, indx) => (
        <SyllabusRow
          key={subTopic.documentId}
          subjectNameLower={subjectNameLower}
          subjectDocumentId={subjectDocumentId}
          subTopic={subTopic}
          topicDocumentId={topicSyllabusPointData.documentId}
          layout={newLayout}
          isStriped={indx % 2 !== 0}
          subTopicSyllabusPointDocumentIdMap={
            topicSyllabusPointMap.subTopics[subTopic.documentId]
          }
          restrictAccess={restrictAccess}
          isComplete={isComplete}
        />
      )),
    [
      subjectNameLower,
      subjectDocumentId,
      topicSyllabusPointData,
      topicSyllabusPointMap,
      restrictAccess,
      isComplete,
    ]
  );

  return (
    <div
      id={topicSyllabusPointData.documentId}
      className={clsx(classes.wrapper, className)}
      style={
        {
          "--st-margin": withoutMargins
            ? `var(--mantine-spacing-lg) 0`
            : `2rem 0 4rem`,
        } as React.CSSProperties
      }
    >
      <Box
        className={classes.progressTrack}
        style={{ background: progressColor, width: `${completionPercentage}%` }}
      />

      <Box className={classes.header}>
        <Title order={2} className={classes.title}>
          {topicMeta.number} - {topicMeta.title}
        </Title>

        <Checkbox
          color="darkblue"
          size="sm"
          label="Mark Complete"
          checked={isComplete}
          onChange={() => {
            if (restrictAccess) return;
            dispatch(
              topicSyllabusSelection({
                subjectDocumentId,
                topicDocumentId: topicSyllabusPointData.documentId,
                topicMeta: topicSyllabusPointMap,
              })
            );
          }}
          styles={{
            label: {
              fontWeight: 600,
              cursor: restrictAccess ? "default" : "pointer",
            },
            input: {
              cursor: restrictAccess ? "not-allowed" : "pointer",
            },
          }}
        />

        <Text
          component="div"
          className={classes.description}
          size="sm"
          dangerouslySetInnerHTML={{
            __html: stripParagraphTag(topicSyllabusPointData.TopicDescription),
          }}
        />

        <Box className={classes.columnHeaders}>
          {["Subtopic", "Understanding"].map((header, indx) => (
            <Text
              key={indx}
              fw={600}
              size="sm"
              style={{ flexBasis: newLayout[indx] }}
            >
              {header}
            </Text>
          ))}
        </Box>
      </Box>

      <div className={classes.rows}>{syllabusRows}</div>
    </div>
  );
}

function createTopicSyllabusPointMap(
  RelatedSubTopics: Pick<SubTopic, "documentId" | "RelatedSyllabusPoints">[]
) {
  const pointMap: TopicSyllabusPointMap = { total: 0, subTopics: {} };
  RelatedSubTopics.forEach((subTopic) => {
    const syllabusPoints = subTopic.RelatedSyllabusPoints;
    pointMap.total += syllabusPoints.length;
    syllabusPoints.forEach((p) => {
      if (!pointMap.subTopics[subTopic.documentId]) {
        pointMap.subTopics[subTopic.documentId] = [p.documentId];
      } else {
        pointMap.subTopics[subTopic.documentId].push(p.documentId);
      }
    });
  });
  return pointMap;
}
